import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LiveSearchService } from '@live/services/live-search.service';
import { RundownService } from '@live/services/rundown.service';
import { LiveActions, LiveApiActions } from '@live/store/actions/live.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { SelectStoryTemplateComponent } from '@src/suite/common/components/create-new-template/select-story-template';
import { ModalRefResult, ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { RichTextSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { LayoutType, SystemLayoutNames } from '@src/suite/common/data-models/service/layout';
import { OPTION_BLANK } from '@src/suite/common/enums/constants';
import { AppName } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { Convertor } from '@src/suite/common/services/convertor';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { StRoDuplicateSlugComponent } from '@src/suite/common/st-full-screen-dialog/st-ro-duplicate-slug/st-ro-duplicate-slug';
import { StRundownPropertiesFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/st-rundown-properties-full-screen/st-rundown-properties-full-screen.component';
import { CloseModalAction, FullscreenActions, OpenFullscreenModalActions, selectAllStoriesMap, selectFullscreenRecordGuid, SidecarEfffectActions } from '@src/suite/common/store/fullscreen';
import { selectTitleSeparator, selectUseMosStartTime } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectListGuids } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, RundownRecordActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import {
    selectRundownRecordByGuid,
    selectRundownRecords,
    selectRundownsWithStories,
    selectStoryRecordByGuid,
    selectStoryRecordsMap,
    selectStoryTemplates,
    selectSubStoriesByParentGuid,
    selectTemplateRundownRecordByGuid,
} from '@src/suite/common/store/records/records.selectors';
import { StoryActions } from '@src/suite/common/store/story/actions/story.actions';
import { selectIsLockedSuccessfullyByGuid } from '@src/suite/common/store/story/selector/story.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { getRundownSecurityPrivsWrite } from '@src/suite/common/utils/record-utils';
import { checkForDuplicateSlugObs } from '@src/suite/common/utils/store-utils';
import { getNonApprovedStoriesMessageContent, KeyName } from '@src/suite/common/utils/util';
import { StMosStorySendComponent } from '@src/suite/live/components/st-mos-story-send/st-mos-story-send.component';
import { SetLayoutArgs } from '@src/suite/live/store/models/live.models';
import { selectStoredLayoutNamesWithoutPredefined } from '@src/suite/live/store/selectors/live.selectors';
import { DateTime } from 'luxon';
import { forkJoin, from, merge, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, debounceTime, exhaustMap, filter, map, mergeMap, switchMap, take, tap, toArray } from 'rxjs/operators';

const RELOAD_DEBOUNCE_TIME = 1000;

export const RUNDOWN_PROPERTIES_DIALOG_ID = 'rundown-properties-dialog';

@Injectable()
export class LiveEffects {
    private actions$: Actions = inject(Actions);
    private liveSearchService: LiveSearchService = inject(LiveSearchService);
    private rundownService: RundownService = inject(RundownService);
    private basicContentService: BasicContentService = inject(BasicContentService);
    private viewStateService: ViewStateService = inject(ViewStateService);
    private dialog: MatDialog = inject(MatDialog);
    private store: Store = inject(Store);
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private modalService: ModalService = inject(ModalService);
    private convertor = inject(Convertor);
    private globalTablesService = inject(GlobalTablesService);
    private dialogRef: MatDialogRef<StRundownPropertiesFullScreenComponent, any> = null;

    private languageSettings = this.store.selectSignal(selectI18N)();

    loadRundownStoredLayouts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownStoredLayouts, RecordHttpActions.loadRundowns),
            switchMap(_ => {
                return this.rundownService.getStoredLayouts().pipe(
                    map(data => {
                        return LiveApiActions.loadRundownStoredLayoutsSuccess({ data });
                    }),
                    catchError(error => of(LiveApiActions.loadRundownStoredLayoutsError({ error })))
                );
            })
        );
    });

    loadRundownLayout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownLayout),
            switchMap(action => {
                if (action.layoutType === LayoutType.Template || action.layoutType === LayoutType.DefaultLayout) {
                    return of(ApplicationActions.noOpAction());
                }
                let name = action.name;
                if (action.layoutType === LayoutType.System || action.layoutType === LayoutType.User) {
                    name = '';
                } else if (action.layoutType === LayoutType.Group) {
                    name = this.nomWebServicesJson.HelloResults.HomeProgram;
                }
                return this.rundownService.getLayout(action.layoutType, name).pipe(
                    filter(data => !!data),
                    map(data => {
                        return LiveApiActions.loadRundownLayoutSuccess({ name: action.name, data: data.data });
                    }),
                    catchError(error => of(LiveApiActions.loadRundownLayoutError({ error })))
                );
            })
        );
    });

    error$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LiveApiActions.loadRundownsError, LiveApiActions.loadCurrentRundownError),
                map(action => {
                    console.error(action.error);
                })
            );
        },
        { dispatch: false }
    );

    saveRundownLayout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.saveRundownLayout),
            switchMap(action => {
                const layouts = this.store.selectSignal(selectStoredLayoutNamesWithoutPredefined)();
                const nameExists = (name: string): boolean => {
                    return !!layouts.find(layoutName => layoutName === name);
                };

                const saveLayout = (layoutOptions: SetLayoutArgs) => {
                    return from(this.rundownService.storeLayoutAs(layoutOptions).then(res => (res.status ? res.data : null))).pipe(
                        filter(data => !!data),
                        concatMap(data => {
                            let layoutName = layoutOptions.LayoutName;
                            switch (layoutOptions.LayoutType) {
                                case LayoutType.User:
                                    layoutName = SystemLayoutNames.USER;
                                    break;
                                case LayoutType.Group:
                                    layoutName = SystemLayoutNames.GROUP;
                                    break;
                            }
                            const actions = [];
                            actions.push(LiveApiActions.loadRundownLayoutSuccess({ name: layoutName, data }));
                            if (layoutOptions.fullscreenArea === FullscreenLayoutAreas.MAIN) {
                                actions.push(
                                    FullscreenActions.setRundownLayout({
                                        layout: {
                                            name: layoutName,
                                            type: layoutOptions.LayoutType,
                                        },
                                        roGuid: layoutOptions.CollectionGuid,
                                    })
                                );
                            } else {
                                actions.push(
                                    SidecarEfffectActions.setRundownLayout({
                                        layout: {
                                            name: layoutName,
                                            type: layoutOptions.LayoutType,
                                        },
                                        roGuid: layoutOptions.CollectionGuid,
                                        sidecar: layoutOptions.fullscreenArea,
                                    })
                                );
                            }
                            return actions;
                        })
                    );
                };
                const layoutOptions = { ...action.params };
                if (layoutOptions.LayoutType === LayoutType.Stored) {
                    return this.modalService
                        .openPromptModal({
                            title: this.languageSettings[I18nKeys.PSTWebSaveAsNewLayoutTitle],
                            placeholder: this.languageSettings[I18nKeys.PSTWebSaveAsNewLayoutInputPlaceholder],
                        })
                        .result.pipe(
                            filter(result => !!result),
                            exhaustMap(result => {
                                layoutOptions.LayoutName = result;
                                const nameAlreadyExists = nameExists(layoutOptions.LayoutName);
                                if (nameAlreadyExists) {
                                    return this.modalService
                                        .openModal({
                                            title: this.languageSettings[I18nKeys.PSTWebModalWarningTitle],
                                            content: this.languageSettings[I18nKeys.PSTWebSaveAsNewLayoutNameAlreadyExists],
                                            yesString: this.languageSettings[I18nKeys.PSTWebOkButtonLabel],
                                            noString: this.languageSettings[I18nKeys.PSTWebCancelButtonLabel],
                                        })
                                        .result.pipe(
                                            filter(alertRes => alertRes === ModalResponseCodes.OK),
                                            exhaustMap(() => {
                                                return saveLayout(layoutOptions);
                                            })
                                        );
                                } else {
                                    return saveLayout(layoutOptions);
                                }
                            })
                        );
                } else {
                    layoutOptions.LayoutName = '';
                    return saveLayout(layoutOptions);
                }
            })
        );
    });

    freezeRundownPages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.freezeRundownPages),
            concatLatestFrom(action => [
                this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.guid)),
                this.store.select(selectI18N),
                this.store.select<StorytellingRundownRecord>(selectTemplateRundownRecordByGuid(action.guid)),
            ]),
            switchMap(([_, record, i18n, templateRecord]) => {
                const selectedRecord = templateRecord ? templateRecord : record;
                if (selectedRecord.FreezePages) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmUnfreezePages],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => selectedRecord)
                        );
                }
                if (selectedRecord?.MOSControl) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmFreezePages],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => selectedRecord)
                        );
                }
                return of(selectedRecord);
            }),
            exhaustMap(rundown => {
                return from(this.rundownService.freezePages(rundown)).pipe(
                    switchMap(_res => {
                        return this.rundownService.getRundown(rundown.Database, rundown.Path.fullPath, rundown.Guid);
                    })
                );
            }),
            switchMap(res => {
                if (res.status) {
                    const action = [];
                    if (res.data?.Path?.isTemplate()) {
                        action.push(RundownRecordActions.updateRundownTemplates({ templates: [res.data] }));
                    } else {
                        action.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                    }
                    return from([...action, StoriesRecordActions.updateStories({ records: res.data.children })]);
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    updateRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                LiveApiActions.onAir,
                LiveApiActions.mosControlActive,
                LiveApiActions.mosReadyToAir,
                LiveApiActions.lockRundown,
                LiveApiActions.updateCurrentRundown,
                LiveApiActions.skipUntimed,
                LiveApiActions.toggleLockingRundownTimingBar
            ),
            concatLatestFrom(action => [
                this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.guid)),
                this.store.select(selectI18N),
                this.store.select<StorytellingRundownRecord>(selectTemplateRundownRecordByGuid(action.guid)),
            ]),
            switchMap(([action, record, i18n, templateRecord]) => {
                const selectedRecord = templateRecord ? templateRecord : record;
                if (action.type === LiveApiActions.onAir.type && selectedRecord?.OnAir) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmTurnOffOnAir],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => ({ action, selectedRecord }))
                        );
                }
                if (action.type === LiveApiActions.mosControlActive.type && selectedRecord?.MOSControl) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmTurnOffMOSControlActive],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => ({ action, selectedRecord }))
                        );
                }
                if (action.type === LiveApiActions.mosReadyToAir.type && selectedRecord?.MOSAir) {
                    return this.modalService
                        .openModal({
                            title: i18n[I18nKeys.PSTWebConfirmTurnOffMOSReadyToAir],
                            content: '',
                            yesString: i18n.PSTWebOkButtonLabel,
                            noString: i18n.PSTWebCancelButtonLabel,
                        })
                        .result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => ({ action, selectedRecord }))
                        );
                }
                return of({ action, selectedRecord });
            }),
            exhaustMap(({ action, selectedRecord }) => {
                let dataToSend = {};
                switch (action.type) {
                    case LiveApiActions.onAir.type:
                        dataToSend = { ...dataToSend, OnAir: !selectedRecord.OnAir };
                        break;
                    case LiveApiActions.mosControlActive.type:
                        dataToSend = { ...dataToSend, MOSControl: !selectedRecord.MOSControl };
                        break;
                    case LiveApiActions.mosReadyToAir.type:
                        dataToSend = { ...dataToSend, MOSAir: !selectedRecord.MOSAir };
                        break;
                    case LiveApiActions.lockRundown.type:
                        dataToSend = { ...dataToSend, Locked: !selectedRecord.Locked, LockedBy: this.nomWebServicesJson.HelloResults?.UserID, LockTime: DateTime.now().toISO() };
                        break;
                    case LiveApiActions.skipUntimed.type:
                        dataToSend = { ...dataToSend, SkipUntimed: !selectedRecord.SkipUntimed };
                        break;
                    case LiveApiActions.toggleLockingRundownTimingBar.type:
                        dataToSend = {
                            ...dataToSend,
                            TimingLocked: !selectedRecord.TimingLocked,
                            TimingLockedBy: this.nomWebServicesJson.HelloResults?.UserID,
                            TimingLockedTime: DateTime.now().toISO(),
                        };
                        break;
                    case LiveApiActions.updateCurrentRundown.type: {
                        const fieldsToUpdate = action?.data;
                        for (const field of fieldsToUpdate) {
                            const enpsField = this.convertor.serializeField(field.value, field.key, selectedRecord.Type);

                            dataToSend[enpsField.key] = enpsField.value;
                        }
                    }
                }

                return from(this.rundownService.updateRundown(selectedRecord, { ...dataToSend, [PSTFieldNames.Type]: selectedRecord.Type })).pipe(
                    switchMap(() => this.rundownService.getRundown(selectedRecord.Database, selectedRecord.Path.fullPath, selectedRecord.Guid))
                );
            }),
            switchMap(res => {
                if (res.status) {
                    const action = [];
                    if (res.data?.Path?.isTemplate()) {
                        action.push(RundownRecordActions.updateRundownTemplates({ templates: [res.data] }));
                    } else {
                        action.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                    }
                    return from([...action, StoriesRecordActions.updateStories({ records: res.data.children })]);
                } else {
                    throwError(() => new Error(res.message));
                }
            }),
            catchError(_ => of(ApplicationActions.noOpAction()))
        );
    });

    updateRundownStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.updateRundownStory),
            concatLatestFrom(action => this.store.select<StorytellingStoryRecord>(selectStoryRecordByGuid(action.guid))),
            concatMap(([action, story]) => {
                const fieldsToUpdate: EnpsField[] = [{ key: action.propID, value: action.value[action.propID] }];
                const separator = this.store.selectSignal(selectTitleSeparator)();
                const titleChanged = action.propID === PSTFieldNames.Slug || action.propID === PSTFieldNames.Segment;
                let title = '';

                if (action.propID === PSTFieldNames.Slug) {
                    const slug = action.value[action.propID];
                    title = slug;
                    const segment = story?.Segment?.trim();
                    if (segment) {
                        title = `${slug}${separator}${segment}`;
                    }
                }

                if (action.propID === PSTFieldNames.Segment) {
                    title = `${story.Slug}${separator}${action.value[action.propID]}`;
                }

                if (titleChanged) {
                    fieldsToUpdate.push({ key: PSTFieldNames.Title, value: title });
                }

                return of(RecordHttpActions.saveRecordChanges({ fieldsToUpdate, record: story }));
            })
        );
    });

    addStoryToRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.addRundownStory),
            mergeMap(action => {
                const shouldRequireLock: boolean = this.store.selectSignal(selectIsLockedSuccessfullyByGuid(action.scriptGuid))();

                const rundowns = this.store.selectSignal(selectRundownsWithStories)();
                const destRundown = rundowns.get(action.guid);

                const allStories = this.store.selectSignal(selectAllStoriesMap)();
                const storyToMove = allStories.get(action.scriptGuid);
                const forceReloadStory = action.forceReloadStory;
                const wasInRundown = storyToMove?.Path?.isInCollection && (!storyToMove?.PSTStoryTopicGUID || storyToMove?.PSTStoryTopicGUID !== storyToMove?.Path?.parentCollectionGuid);
                let insertBeforeGuid = action.insertBeforeGuid;
                const requiresApproval = destRundown?.ApprovedScriptsOnly;

                if (requiresApproval && !storyToMove?.Approved) {
                    const content = getNonApprovedStoriesMessageContent(storyToMove);
                    this.modalService.openAlert({
                        title: this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesTitleAlert],
                        content: `${this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesMessageAlert]} ${content}`,
                        okString: this.languageSettings?.[I18nKeys.PSTWebOkButtonLabel],
                    });

                    return of(ApplicationActions.noOpAction());
                }

                // If the story has substories and move only primary or primary with substories option
                const subStoriesMap = this.store.selectSignal(selectSubStoriesByParentGuid)();
                const subStories = (subStoriesMap?.get(action.scriptGuid) || []).filter(s => {
                    const isInRundown = s.Path?.isInCollection && (!s.PSTStoryTopicGUID || s.PSTStoryTopicGUID !== s.Path?.parentCollectionGuid);
                    return !isInRundown;
                });

                const subStoriesSelection =
                    subStories.length > 0 && !action.subStories
                        ? (() => {
                              const modalRef: ModalRefResult = this.modalService.openRadioOption({
                                  title: this.languageSettings?.[I18nKeys.PSTWebMoveToRundownTitle],
                                  message: this.languageSettings?.[I18nKeys.PSTWebMoveToRundownMessage],
                                  options: [
                                      { id: '1', title: this.languageSettings?.[I18nKeys.PSTWebMoveAllStoriesToRundown] },
                                      { id: '2', title: this.languageSettings?.[I18nKeys.PSTWebMovePrimaryStoryOnly] },
                                  ],
                                  confirmLabel: this.languageSettings?.[I18nKeys.PSTWebConfirmButtonLabel],
                                  cancelLabel: this.languageSettings?.[I18nKeys.PSTWebCancelButtonLabel],
                              });

                              if (!modalRef) {
                                  return of(null);
                              }

                              return modalRef.result.pipe(
                                  take(1),
                                  map(response => {
                                      if (!response || response === ModalResponseCodes.CANCEL) {
                                          return null;
                                      }
                                      return response === '1' ? subStories.map(s => ({ scriptDatabase: s.Database, scriptPath: s.Path?.fullPath, scriptGuid: s.Guid })) : undefined;
                                  })
                              );
                          })()
                        : of(action.subStories);

                return subStoriesSelection.pipe(
                    concatMap(resolvedSubStories => {
                        if (resolvedSubStories === null) {
                            return of(LiveApiActions.addRundownStoryError({ error: '', scriptGuid: action.scriptGuid }));
                        }
                        const selectedSubStories = resolvedSubStories;
                        // TODO: this should be moved to some generic method, or directly into the shouldReplaceStoryObservable
                        return this.getFullStoryRecord(storyToMove, forceReloadStory).pipe(
                            concatMap(fullStory => {
                                const text = fullStory?.Text;
                                const textCommands = fullStory?.TextCommands;
                                const propertiesToOverride = { ...action.propertiesToOverride };
                                propertiesToOverride[PSTFieldNames.PSTDraftText] = RichTextSerializer.instance.serialize(text);
                                if (textCommands) {
                                    const textCommandKeys = Object.keys(textCommands);
                                    const textCommandValues = Object.values(textCommands);
                                    if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                        propertiesToOverride['TextCommandKeys'] = textCommandKeys;
                                        propertiesToOverride['TextCommandValues'] = textCommandValues;
                                    }
                                }

                                return this.shouldReplaceStoryObservable(destRundown, fullStory).pipe(
                                    map(replaceStoryGuid => {
                                        const storyToRemoveIdx = destRundown.children.findIndex(v => v.Guid === replaceStoryGuid);
                                        if (storyToRemoveIdx > -1) {
                                            insertBeforeGuid = destRundown.children[storyToRemoveIdx + 1]?.Guid;
                                        }
                                        return replaceStoryGuid;
                                    }),
                                    concatMap(replaceStoryGuid => {
                                        if (replaceStoryGuid) {
                                            const storyToRemove = allStories.get(replaceStoryGuid);
                                            if (storyToRemove && storyToRemove.Guid !== '0') {
                                                return this.basicContentService
                                                    .deleteBasicContent({
                                                        database: storyToRemove.Database,
                                                        path: storyToRemove.Path?.fullPath,
                                                        guid: storyToRemove.Guid,
                                                        type: storyToRemove.Type,
                                                        record: storyToRemove,
                                                    })
                                                    .pipe(map(res => ({ success: !!res.status, storyToRemove })));
                                            }
                                        }
                                        return of({ success: true, storyToRemove: null as StorytellingStoryRecord | null });
                                    }),
                                    filter(({ success }) => !!success),
                                    concatMap(({ storyToRemove }) => {
                                        return from(
                                            this.rundownService.addStoryToRundown({
                                                ro: destRundown,
                                                scriptDatabase: action.scriptDatabase,
                                                scriptPath: action.scriptPath,
                                                scriptGuid: action.scriptGuid,
                                                insertBeforeGuid,
                                                fieldsToUpdate: propertiesToOverride,
                                            })
                                        ).pipe(
                                            switchMap(res => {
                                                const actions = [];
                                                actions.push(RundownRecordActions.updateRundowns({ records: [res.data] }));

                                                if (storyToRemove) {
                                                    actions.push(StoriesRecordActions.deleteStories({ records: [{ Guid: storyToRemove?.Guid }] }));
                                                }

                                                const copiedScriptIdx = res.data.children.findIndex(script => script.OriginalGUID === action.scriptGuid);
                                                const wasCopyAndDeleted = (wasInRundown && copiedScriptIdx > -1) || action.scriptDatabase !== destRundown.Database;

                                                // Update PSTPrimaryStoryGUID for substories that the server auto-moved with the parent
                                                const enrichedChildren = res.data.children.map(child => {
                                                    const childStory = child as StorytellingStoryRecord;
                                                    if (childStory.PSTPrimaryStoryGUID && childStory.PSTPrimaryStoryGUID === action.scriptGuid) {
                                                        return { ...child, PSTPrimaryStoryGUID: action.scriptGuid } as StorytellingStoryRecord;
                                                    }
                                                    return child;
                                                });
                                                actions.push(StoriesRecordActions.updateStories({ records: enrichedChildren }));
                                                if (wasCopyAndDeleted) {
                                                    actions.push(
                                                        StoriesRecordActions.deleteStories({
                                                            records: [
                                                                {
                                                                    Guid: action.scriptGuid,
                                                                },
                                                            ],
                                                        })
                                                    );

                                                    // Update the listGuids with the new script guid, if it exists
                                                    const listGuids = [...(this.store.selectSignal(selectListGuids)() || [])];
                                                    const oldScriptIdx = listGuids?.indexOf(action.scriptGuid);
                                                    if (oldScriptIdx > -1 && copiedScriptIdx > -1) {
                                                        listGuids?.splice(oldScriptIdx, 1, res.data.children[copiedScriptIdx].Guid);
                                                        actions.push(ListActions.setListGuids({ listGuids: new Set(listGuids) }));
                                                    }

                                                    // find the story that was just created and update the fullscreen record guid if it is the previous one
                                                    // find the story in the res.data.children
                                                    const story = res.data.children.find(script => script.OriginalGUID === action.scriptGuid);
                                                    if (story) {
                                                        if (shouldRequireLock) {
                                                            actions.push(StoryActions.requireLock({ storyGuid: story.Guid }));
                                                        }
                                                        actions.push(FullscreenActions.openStory({ recordGuid: story.Guid, recordType: ObjectTypes.SCRIPT }));
                                                    }
                                                }

                                                // Process substories sequentially using the same destRundown
                                                if (selectedSubStories?.length > 0) {
                                                    // Track what was in the dest rundown BEFORE any moves (to identify auto-moved substories)
                                                    const preExistingInDest = new Set(destRundown.children?.map(c => c.Guid) || []);
                                                    preExistingInDest.add(action.scriptGuid); // exclude the parent too
                                                    const oldSubStoryGuids = new Set(selectedSubStories.map(s => s.scriptGuid));
                                                    return from(selectedSubStories).pipe(
                                                        concatMap(subStory => {
                                                            const subStoryRecord = allStories.get(subStory.scriptGuid);
                                                            return from(
                                                                this.rundownService.addStoryToRundown({
                                                                    ro: destRundown,
                                                                    scriptDatabase: subStory.scriptDatabase,
                                                                    scriptPath: subStory.scriptPath,
                                                                    scriptGuid: subStory.scriptGuid,
                                                                    insertBeforeGuid: action.insertBeforeGuid,
                                                                    fieldsToUpdate: { [PSTFieldNames.Title]: subStoryRecord?.Title || '' },
                                                                })
                                                            );
                                                        }),
                                                        toArray(),
                                                        concatMap(subResults => {
                                                            const lastResult = subResults[subResults.length - 1];
                                                            if (lastResult?.data) {
                                                                // First delete the old substory records so they disappear from the topic's stories list
                                                                const subStoriesToDelete = selectedSubStories.map(s => ({ Guid: s.scriptGuid }));
                                                                actions.push(StoriesRecordActions.deleteStories({ records: subStoriesToDelete }));
                                                                // Then update with the final rundown state (re-adds them as rundown children)
                                                                actions.push(RundownRecordActions.updateRundowns({ records: [lastResult.data] }));

                                                                // Identify substory copies and update PSTPrimaryStoryGUID:
                                                                // - child is a copy of one of the old substories
                                                                // - child's PSTPrimaryStoryGUID points to the old parent
                                                                // - child is new in the rundown (wasn't there before the move)
                                                                const updatedChildren = lastResult.data.children.map(child => {
                                                                    const childStory = child as StorytellingStoryRecord;
                                                                    const isSubstoryCopy =
                                                                        (child.OriginalGUID && oldSubStoryGuids.has(child.OriginalGUID)) ||
                                                                        (childStory.PSTPrimaryStoryGUID && childStory.PSTPrimaryStoryGUID === action.scriptGuid) ||
                                                                        !preExistingInDest.has(child.Guid);
                                                                    if (isSubstoryCopy && child.Guid !== action.scriptGuid) {
                                                                        return { ...child, PSTPrimaryStoryGUID: action.scriptGuid } as StorytellingStoryRecord;
                                                                    }
                                                                    return child;
                                                                });
                                                                actions.push(StoriesRecordActions.updateStories({ records: updatedChildren }));
                                                            }
                                                            // Reload the destination rundown to get complete Slug/Segment data for all children
                                                            actions.push(
                                                                LiveApiActions.reloadRundowns({
                                                                    sourceROs: [
                                                                        {
                                                                            Guid: destRundown?.Guid,
                                                                            Database: destRundown?.Database,
                                                                            Path: destRundown?.Path,
                                                                        },
                                                                    ],
                                                                })
                                                            );
                                                            actions.push(LiveApiActions.addRundownStorySuccess({ data: destRundown, scriptGuid: action.scriptGuid }));
                                                            return actions;
                                                        })
                                                    );
                                                }

                                                actions.push(LiveApiActions.addRundownStorySuccess({ data: destRundown, scriptGuid: action.scriptGuid }));
                                                return actions;
                                            }),
                                            catchError(error => [LiveApiActions.updateRundownStoryError({ error }), LiveApiActions.addRundownStoryError({ error, scriptGuid: action.scriptGuid })])
                                        );
                                    })
                                );
                            })
                        );
                    })
                );
            })
        );
    });

    insertRundownRow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.insertRundownRow),
            concatLatestFrom(action => [this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.collectionGuid)), this.store.select(selectStoryTemplates)]),
            exhaustMap(([action, rundown, storyTemplates]) => {
                if (rundown?.ShowTemplatesOnROInsert) {
                    const existingDialog = this.dialog.getDialogById('st-select-template-dialog');
                    if (existingDialog) {
                        existingDialog.close();
                    }
                    const width = '35rem';
                    const height = '30rem';
                    const panelClass = 'st-select-template-dialog';
                    const dialogRef = this.dialog.open(SelectStoryTemplateComponent, {
                        width,
                        height,
                        hasBackdrop: true,
                        panelClass,
                        id: 'st-select-template-dialog',
                        data: {
                            title: action.fieldsToUpdate?.[PSTFieldNames.Title],
                        },
                    });

                    return dialogRef.afterClosed().pipe(
                        exhaustMap(modalRes => {
                            if (modalRes?.template && modalRes.title) {
                                const template = storyTemplates.find(t => t.Guid === modalRes.template?.value);
                                if (template.Guid === OPTION_BLANK) {
                                    action.fieldsToUpdate[PSTFieldNames.Title] = modalRes.title;
                                    return from(
                                        this.rundownService.insertRowInRundown(rundown, action.database, action.collectionPath, action.collectionGuid, action.insertBeforeGuid, action.fieldsToUpdate)
                                    ).pipe(
                                        exhaustMap(res => {
                                            return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
                                        }),
                                        catchError(error => of(LiveApiActions.updateRundownStoryError({ error })))
                                    );
                                }
                                return from(
                                    this.nomWebServicesJson.copyScriptToCollection<StorytellingRundownRecord>(
                                        action.database,
                                        action.collectionPath,
                                        action.collectionGuid,
                                        ObjectTypes.RUNDOWN,
                                        template?.Database,
                                        template?.Path?.fullPath,
                                        template?.Guid,
                                        ObjectTypes.SCRIPT,
                                        [
                                            { FieldName: PSTFieldNames.Title, FieldValue: modalRes.title },
                                            { FieldName: PSTFieldNames.TargetPublicationDate, FieldValue: DateTime.now() },
                                        ],
                                        action.insertBeforeGuid
                                    )
                                ).pipe(
                                    switchMap(_ => {
                                        return this.rundownService.getRundown(action.database, action.collectionPath, action.collectionGuid);
                                    }),
                                    switchMap(res => {
                                        return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
                                    }),
                                    catchError(error => of(LiveApiActions.updateRundownStoryError({ error })))
                                );
                            }
                            return of();
                        })
                    );
                } else {
                    if (rundown?.ScriptTemplate) {
                        const template = storyTemplates.find(t => t.Guid === rundown?.ScriptTemplate);
                        return from(
                            this.nomWebServicesJson.copyScriptToCollection<StorytellingRundownRecord>(
                                action.database,
                                action.collectionPath,
                                action.collectionGuid,
                                ObjectTypes.RUNDOWN,
                                template?.Database,
                                template?.Path?.fullPath,
                                template?.Guid,
                                ObjectTypes.SCRIPT,
                                [
                                    { FieldName: PSTFieldNames.Title, FieldValue: action.fieldsToUpdate?.[PSTFieldNames.Title] },
                                    { FieldName: PSTFieldNames.TargetPublicationDate, FieldValue: DateTime.now() },
                                ],
                                action.insertBeforeGuid
                            )
                        ).pipe(
                            switchMap(_ => {
                                return this.rundownService.getRundown(action.database, action.collectionPath, action.collectionGuid);
                            }),
                            switchMap(res => {
                                return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
                            })
                        );
                    } else {
                        if (rundown?.ApprovedScriptsOnly) {
                            this.modalService.openAlert({
                                title: this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesTitleAlert],
                                content: `${this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesMessageAlert]}`,
                                okString: this.languageSettings?.[I18nKeys.PSTWebOkButtonLabel],
                            });

                            return of(ApplicationActions.noOpAction());
                        }

                        return from(
                            this.rundownService.insertRowInRundown(rundown, action.database, action.collectionPath, action.collectionGuid, action.insertBeforeGuid, action.fieldsToUpdate)
                        ).pipe(
                            switchMap(res => {
                                return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
                            }),
                            catchError(error => of(LiveApiActions.updateRundownStoryError({ error })))
                        );
                    }
                }
            })
        );
    });

    loadRundownViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownViewStates),
            switchMap(() => {
                return this.viewStateService.loadViewStateRecords(AppName.LIVE).pipe(
                    switchMap(({ data }) => {
                        return of(LiveApiActions.loadRundownViewStatesSuccess({ data }), ApplicationActions.setCurrentRundownViewState({ guid: data?.[0]?.Guid }));
                    }),
                    catchError(error => of(LiveApiActions.loadRundownsError({ error })))
                );
            })
        );
    });

    onRoLayoutChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setRundownLayout, FullscreenActions.setRundownLayout),
            map(action => LiveApiActions.loadRundownLayout({ layoutType: action.layout.type, name: action.layout.name }))
        );
    });

    loadRundownTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownTemplates, RecordHttpActions.loadRundowns),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            mergeMap(([_, vs]) => {
                return from(this.rundownService.loadRundownTemplates(vs)).pipe(
                    map(templates => RundownRecordActions.setRundownTemplates({ templates })),
                    catchError(error => of(LiveApiActions.loadRundownTemplatesError({ error: error })))
                );
            })
        );
    });

    createNewRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.createNewRundown),
            mergeMap(action => {
                const database = action?.record?.Database || this.nomWebServicesJson.PSTServer;
                const groupId = action?.record?.Path?.groupId || this.nomWebServicesJson.HelloResults.HomeProgram;
                const path = `P_${groupId}\\W`;
                return from(this.rundownService.createRundown(action.record, database, path)).pipe(
                    mergeMap(res => {
                        const actions = [
                            RundownRecordActions.updateRundowns({ records: [res.data] }),
                            ListActions.addNewListGuids({ listGuids: [res.data.Guid] }),
                            OpenFullscreenModalActions.openRundownFullscreenDialog({
                                recordGuid: res.data.Guid,
                                recordType: res.data.Type,
                            }),
                            PlaybookApiActions.createRecordDone(),
                        ];

                        return actions;
                    }),
                    catchError(error => of(LiveApiActions.loadRundownTemplatesError({ error: error })))
                );
            })
        );
    });

    openNewRundownDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.openRundownPropertiesDialog),
            switchMap(action => {
                const width = '38.625rem';
                const height = '90vh';
                this.dialogRef = this.dialog.open(StRundownPropertiesFullScreenComponent, {
                    id: RUNDOWN_PROPERTIES_DIALOG_ID,
                    height,
                    width,
                    hasBackdrop: true,
                    disableClose: true,
                    autoFocus: true,
                    data: { record: action.record, popUpTitle: action.popUpTitle },
                });
                const backdropObs = this.dialogRef.backdropClick().pipe(
                    tap(event => {
                        event.preventDefault();
                    }),
                    map(() => LiveApiActions.closeNewRundownDialog())
                );
                const keydownObs = this.dialogRef.keydownEvents().pipe(
                    filter((event: KeyboardEvent) => event.key === KeyName.Escape),
                    tap(event => {
                        event.preventDefault();
                    }),
                    map(() => LiveApiActions.closeNewRundownDialog())
                );
                return merge(keydownObs, backdropObs);
            })
        );
    });

    closeNewRundownDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.closeNewRundownDialog),
            switchMap(() => {
                const languageSettings = this.store.selectSignal(selectI18N);
                const modalRef: ModalRefResult = this.modalService.openModal({
                    title: languageSettings().PSTWebConfirmCancelShort,
                    content: languageSettings().PSTWebConfirmCancel,
                });

                if (!modalRef) {
                    return of(ApplicationActions.noOpAction());
                }
                return modalRef.result.pipe(
                    take(1),
                    exhaustMap(modalRes => {
                        if (modalRes === ModalResponseCodes.OK) {
                            this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                        }
                        return [ApplicationActions.noOpAction()];
                    }),
                    catchError(() => {
                        return [ApplicationActions.noOpAction()];
                    })
                );
            })
        );
    });

    onRoLayoutDelete$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.deleteRundownLayout),
            switchMap(action => {
                const languageSettings = this.store.selectSignal(selectI18N)();
                const content = languageSettings[I18nKeys.PSTWebConfirmDeleteLayout].replace('{layoutName}', action.layoutName);
                return this.modalService
                    .openModal({
                        title: languageSettings[I18nKeys.PSTWebModalWarningTitle],
                        content,
                        yesString: languageSettings.PSTWebOkButtonLabel,
                        noString: languageSettings.PSTWebCancelButtonLabel,
                    })
                    .result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        switchMap(() => {
                            return from(this.rundownService.deleteStoredLayout(action.layoutName)).pipe(
                                filter(res => !!res),
                                map(() => {
                                    return LiveActions.removeStoredLayout({ layoutName: action.layoutName });
                                })
                            );
                        })
                    );
            })
        );
    });

    onRoStoriesLockedByAnotherUser$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LiveApiActions.rundownStoriesLockedByAnotherUser),
                switchMap(action => {
                    const i18n = this.store.selectSignal(selectI18N)();
                    const content = `${i18n.PSTWebStoryLockedAt} ${action?.lockTime?.toLocaleString(DateTime.DATETIME_SHORT)} by ${action?.lockedBy}. \n ${i18n.PSTWebPleaseTryAgainLater}.`;
                    return this.modalService.openAlert({ title: i18n.PSTLockStoryAlertMessage, content, okString: i18n.PSTWebOkButtonLabel, disableClose: true }).result;
                })
            );
        },
        { dispatch: false }
    );

    lockUnLockAllRundownStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.lockUnLockRundownStories),
            switchMap(action => {
                return from(this.rundownService.lockUnLockRundownStories(action.rundown.Database, action.rundown.Path.fullPath, action.rundown.Guid, action.storiesLockStatus)).pipe(
                    filter(res => res.status),
                    switchMap(() => {
                        return from(this.rundownService.getRundown(action.rundown.Database, action.rundown.Path.fullPath, action.rundown.Guid));
                    }),
                    switchMap(res => {
                        const actions = [];
                        if (res.status) {
                            if (res.data?.Path?.isTemplate()) {
                                actions.push(RundownRecordActions.updateRundownTemplates({ templates: [res.data] }));
                            } else {
                                actions.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                            }
                            return from([...actions, StoriesRecordActions.updateStories({ records: res.data.children })]);
                        }
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    shouldReplaceStoryObservable = (destRundown: StorytellingRundownRecord, story: StorytellingStoryRecord): Observable<string> => {
        const titleSeparator = this.store.selectSignal(selectTitleSeparator)();
        return checkForDuplicateSlugObs(destRundown, story, titleSeparator).pipe(
            concatMap(duplicateTitleStory => {
                if (!duplicateTitleStory) {
                    return of(null);
                }
                if (destRundown?.DupeSlugsOptions) {
                    return this.dialog
                        .open(StRoDuplicateSlugComponent, {
                            hasBackdrop: true,
                            disableClose: true,
                            autoFocus: true,
                            panelClass: 'st-ro-duplicate-slug',
                            data: {
                                story: story,
                                rundown: destRundown,
                            },
                        })
                        .afterClosed()
                        .pipe(
                            filter(res => res && res.status !== ModalResponseCodes.CANCEL),
                            map(res => {
                                if (res.status === ModalResponseCodes.CUSTOM) {
                                    story.Title = res?.data?.Title?.trim();
                                    return null;
                                }
                                return duplicateTitleStory?.Guid;
                            })
                        );
                }
                return of(duplicateTitleStory?.Guid);
            })
        );
    };

    onMoveScriptsToCollection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.moveScriptFromRundownToRundown),
            concatMap(({ stories, destGuid, insertBeforeGuid, sourceRO }) => {
                const storiesMap = this.store.selectSignal(selectStoryRecordsMap)();
                const rundowns = this.store.selectSignal(selectRundownsWithStories)();
                const destRundown = rundowns.get(destGuid);
                const isRundownDisabled = getRundownSecurityPrivsWrite({ rec: destRundown, globalTablesService: this.globalTablesService });
                if (!isRundownDisabled) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    this.modalService.openAlert({
                        title: languageSettings.PSTWebInsufficientPrivilegesTitleAlert,
                        content: languageSettings.PSTWebInsufficientPrivilegesMessageAlert,
                        okString: languageSettings.PSTWebOkButtonLabel,
                    });
                    return of(ApplicationActions.noOpAction());
                }

                const updatedDestRundown = {
                    ...destRundown,
                };

                const requiresApproval = destRundown?.ApprovedScriptsOnly;

                // return if moving contains unapproved story to a rundown with ApprovedScriptsOnly enabled
                if (requiresApproval) {
                    const unapprovedStory = stories.find(story => !story?.Approved);
                    if (unapprovedStory) {
                        const content = getNonApprovedStoriesMessageContent(unapprovedStory);
                        this.modalService.openAlert({
                            title: this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesTitleAlert],
                            content: `${this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesMessageAlert]} ${content}`,
                            okString: this.languageSettings?.[I18nKeys.PSTWebOkButtonLabel],
                        });

                        return of(ApplicationActions.noOpAction());
                    }
                }

                // Identify substories whose parent is also being moved — skip server persist for them in Phase 1
                const parentGuidsInBatch = new Set(stories.map(s => s.Guid));
                const subStoryGuidsBeingMoved = new Set(
                    stories
                        .filter(s => {
                            const fullRecord = storiesMap.get(s.Guid);
                            return fullRecord?.PSTPrimaryStoryGUID && parentGuidsInBatch.has(fullRecord.PSTPrimaryStoryGUID);
                        })
                        .map(s => s.Guid)
                );

                const buildStoryOperation = (story: Partial<StorytellingStoryRecord>, sharedGuidMapping: Map<string, string>) =>
                    this.getFullStoryRecord(story).pipe(
                        concatMap(fullStory => {
                            return this.shouldReplaceStoryObservable(destRundown, fullStory).pipe(
                                map(replaceStoryGuid => {
                                    const storyToRemoveIdx = destRundown.children.findIndex(v => v.Guid === replaceStoryGuid);
                                    if (storyToRemoveIdx > -1) {
                                        insertBeforeGuid = destRundown.children[storyToRemoveIdx + 1]?.Guid;
                                    }
                                    return replaceStoryGuid;
                                }),
                                concatMap(replaceStoryGuid => {
                                    if (replaceStoryGuid) {
                                        const storyToRemove = storiesMap.get(replaceStoryGuid);
                                        if (storyToRemove) {
                                            return this.basicContentService
                                                .deleteBasicContent({
                                                    database: storyToRemove.Database,
                                                    path: storyToRemove.Path?.fullPath,
                                                    guid: storyToRemove.Guid,
                                                    type: storyToRemove.Type,
                                                })
                                                .pipe(map(res => ({ success: !!res.status, storyToRemoveGuid: storyToRemove?.Guid, deletedResponse: res.data })));
                                        }
                                    }
                                    return of({ success: true, storyToRemoveGuid: null as string | null, deletedResponse: null as RemoveRecordResponse | null });
                                }),
                                filter(res => !!res.success),
                                concatMap(({ storyToRemoveGuid, deletedResponse }) => {
                                    const text = fullStory?.Text;
                                    const textCommands = fullStory?.TextCommands;
                                    const propertiesToOverride = {};
                                    propertiesToOverride[PSTFieldNames.PSTDraftText] = RichTextSerializer.instance.serialize(text);
                                    propertiesToOverride[PSTFieldNames.Title] = fullStory?.Title;
                                    if (fullStory?.PSTPrimaryStoryGUID) {
                                        // Remap PSTPrimaryStoryGUID if the parent was already moved (phase 2)
                                        const remappedGuid = sharedGuidMapping.get(fullStory.PSTPrimaryStoryGUID);
                                        propertiesToOverride[PSTFieldNames.PSTPrimaryStoryGUID] = remappedGuid || fullStory.PSTPrimaryStoryGUID;
                                    }
                                    if (textCommands) {
                                        const textCommandKeys = Object.keys(textCommands);
                                        const textCommandValues = Object.values(textCommands);
                                        if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                            propertiesToOverride['TextCommandKeys'] = textCommandKeys;
                                            propertiesToOverride['TextCommandValues'] = textCommandValues;
                                        }
                                    }

                                    const actions: Action[] = [];
                                    actions.push(
                                        LiveApiActions.reloadRundowns({
                                            sourceROs: [
                                                sourceRO,
                                                {
                                                    Guid: updatedDestRundown?.Guid,
                                                    Database: updatedDestRundown?.Database,
                                                    Path: updatedDestRundown?.Path,
                                                },
                                            ],
                                        })
                                    );
                                    const updatedRundown = deletedResponse?.updatedRecords?.rundowns?.get(updatedDestRundown?.Guid);
                                    if (updatedRundown) {
                                        updatedDestRundown.Sequence = [...updatedRundown.Sequence];
                                        updatedDestRundown.children = [...updatedRundown.children];
                                    }
                                    if (storyToRemoveGuid) {
                                        actions.push(StoriesRecordActions.deleteStories({ records: [{ Guid: storyToRemoveGuid }] }));
                                    }
                                    const shouldRequireLock: boolean = this.store.selectSignal(selectIsLockedSuccessfullyByGuid(fullStory.Guid))();
                                    return from(
                                        this.rundownService.addStoryToRundown({
                                            ro: destRundown,
                                            scriptDatabase: fullStory.Database,
                                            scriptPath: fullStory.Path?.fullPath,
                                            scriptGuid: fullStory.Guid,
                                            insertBeforeGuid: insertBeforeGuid,
                                            fieldsToUpdate: propertiesToOverride,
                                        })
                                    ).pipe(
                                        exhaustMap(res => {
                                            // !IMPORTANT: if the story wasn't in a Rundown, we actually don't create a new one when moving, but using the original one
                                            if (res.status && res.data) {
                                                const copiedScriptIdx = (res?.data?.children || []).findIndex(script => script.OriginalGUID === fullStory?.Guid);
                                                const wasCopyAndDeleted = copiedScriptIdx > -1;
                                                const newStoryGuid = wasCopyAndDeleted ? res.data.children[copiedScriptIdx].Guid : null;
                                                const fullscreenStoryRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                                                if (wasCopyAndDeleted) {
                                                    actions.push(
                                                        StoriesRecordActions.deleteStories({
                                                            records: [
                                                                {
                                                                    Guid: fullStory?.Guid,
                                                                },
                                                            ],
                                                        })
                                                    );

                                                    // Update substories to point to the new parent GUID
                                                    const subStoriesMap = this.store.selectSignal(selectSubStoriesByParentGuid)();
                                                    const subStoriesForParent = subStoriesMap?.get(fullStory.Guid) || [];
                                                    if (subStoriesForParent.length > 0) {
                                                        // Filter out auto-moved substories (they'll be handled in enrichedScripts below)
                                                        const autoMovedOriginalGuids = new Set((res.data.children || []).filter(c => c.OriginalGUID).map(c => c.OriginalGUID));
                                                        const subStoriesToUpdate = subStoriesForParent
                                                            .filter(s => !autoMovedOriginalGuids.has(s.Guid) && !subStoryGuidsBeingMoved.has(s.Guid))
                                                            .map(story => ({ ...story, PSTPrimaryStoryGUID: newStoryGuid }));
                                                        if (subStoriesToUpdate.length > 0) {
                                                            actions.push(StoriesRecordActions.updateStories({ records: subStoriesToUpdate }));
                                                            // Persist PSTPrimaryStoryGUID to the server for substories that still exist
                                                            subStoriesToUpdate.forEach(subStory => {
                                                                actions.push(
                                                                    RecordHttpActions.saveRecordChanges({
                                                                        record: subStory,
                                                                        fieldsToUpdate: [{ key: PSTFieldNames.PSTPrimaryStoryGUID, value: newStoryGuid }],
                                                                    })
                                                                );
                                                            });
                                                        }
                                                    }

                                                    // Update the listGuids with the new script guid, if it exists
                                                    const listGuids = [...(this.store.selectSignal(selectListGuids)() || [])];
                                                    const oldScriptIdx = listGuids?.indexOf(fullStory?.Guid);
                                                    if (oldScriptIdx > -1 && copiedScriptIdx > -1) {
                                                        listGuids?.splice(oldScriptIdx, 1, res.data.children[copiedScriptIdx].Guid);
                                                        actions.push(ListActions.setListGuids({ listGuids: new Set(listGuids) }));
                                                    }

                                                    // find the story that was just created and update the fullscreen record guid if it is the previous one
                                                    if (fullscreenStoryRecordGuid === fullStory?.Guid) {
                                                        // find the story in the res.data.children
                                                        const storyToLock = destRundown.children.find(script => script.OriginalGUID === fullscreenStoryRecordGuid);
                                                        if (storyToLock && shouldRequireLock) {
                                                            actions.push(StoryActions.requireLock({ storyGuid: storyToLock.Guid }));
                                                        }
                                                    }
                                                }
                                                // Build mapping of old GUIDs to new GUIDs for cross-DB remapping
                                                (res.data.children || []).forEach(child => {
                                                    if (child.OriginalGUID) {
                                                        sharedGuidMapping.set(child.OriginalGUID, child.Guid);
                                                    }
                                                });
                                                const scriptsToInsert = res?.data?.children?.filter(s => !storiesMap?.has(s.Guid));
                                                if (scriptsToInsert?.length > 0) {
                                                    // Enrich new records with PSTPrimaryStoryGUID from originals and remap to new parent GUID
                                                    const enrichedScripts = scriptsToInsert.map(script => {
                                                        const enriched = { ...script } as StorytellingStoryRecord;
                                                        if (script.OriginalGUID) {
                                                            const originalRecord = storiesMap.get(script.OriginalGUID);
                                                            if (originalRecord) {
                                                                if (originalRecord.PSTPrimaryStoryGUID && !enriched.PSTPrimaryStoryGUID) {
                                                                    enriched.PSTPrimaryStoryGUID = originalRecord.PSTPrimaryStoryGUID;
                                                                }
                                                            }
                                                        }
                                                        // Remap PSTPrimaryStoryGUID from old parent GUID to new parent GUID (cross-DB move)
                                                        if (enriched.PSTPrimaryStoryGUID && sharedGuidMapping.has(enriched.PSTPrimaryStoryGUID)) {
                                                            enriched.PSTPrimaryStoryGUID = sharedGuidMapping.get(enriched.PSTPrimaryStoryGUID);
                                                        }
                                                        return enriched;
                                                    });
                                                    actions.push(
                                                        StoriesRecordActions.updateStories({
                                                            records: enrichedScripts,
                                                        })
                                                    );
                                                    // Persist remapped PSTPrimaryStoryGUID to the server for auto-moved substories
                                                    enrichedScripts.forEach(script => {
                                                        const enriched = script as StorytellingStoryRecord;
                                                        if (enriched.PSTPrimaryStoryGUID && enriched.OriginalGUID && sharedGuidMapping.has(enriched.OriginalGUID)) {
                                                            actions.push(
                                                                RecordHttpActions.saveRecordChanges({
                                                                    record: enriched,
                                                                    fieldsToUpdate: [{ key: PSTFieldNames.PSTPrimaryStoryGUID, value: enriched.PSTPrimaryStoryGUID }],
                                                                })
                                                            );
                                                        }
                                                    });
                                                }

                                                actions.push(
                                                    RundownRecordActions.updateRundowns({
                                                        records: [res?.data],
                                                    })
                                                );
                                            } else {
                                                actions.push(
                                                    RundownRecordActions.updateRundowns({
                                                        records: [updatedDestRundown],
                                                    })
                                                );
                                            }
                                            return of(actions);
                                        })
                                    );
                                })
                            );
                        })
                    );

                // Process stories sequentially to maintain original order.
                // Parents are processed before their substories (natural rundown order),
                // so sharedGuidMapping is populated by the time substories are processed.
                const sharedGuidMapping = new Map<string, string>();

                return from(stories).pipe(
                    concatMap(story => buildStoryOperation(story, sharedGuidMapping)),
                    toArray(),
                    map(actionsArrays => actionsArrays.flat()),
                    mergeMap(actions => from(actions))
                );
            })
        );
    });

    onCopyScriptsToCollection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.copyScriptFromRundownToRundown),
            concatMap(action => {
                const stories = this.store.selectSignal(selectStoryRecordsMap)();
                const rundowns = this.store.selectSignal(selectRundownsWithStories)();
                const destRundown = rundowns.get(action.destGuid);
                const isRundownDisabled = getRundownSecurityPrivsWrite({ rec: destRundown, globalTablesService: this.globalTablesService });
                if (!isRundownDisabled) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    this.modalService.openAlert({
                        title: languageSettings.PSTWebInsufficientPrivilegesTitleAlert,
                        content: languageSettings.PSTWebInsufficientPrivilegesMessageAlert,
                        okString: languageSettings.PSTWebOkButtonLabel,
                    });
                    return of(ApplicationActions.noOpAction());
                }

                const updatedDestRundown = {
                    ...destRundown,
                };

                let insertBeforeGuid = action.insertBeforeGuid;
                // skip if copy record contains unapproved story to a rundown with ApprovedScriptsOnly enabled
                const requiresApproval = destRundown?.ApprovedScriptsOnly;

                return from(action.stories).pipe(
                    concatMap(rec =>
                        this.getFullStoryRecord(rec).pipe(
                            concatMap(story => {
                                const isUnapproved = !story?.Approved;

                                if (requiresApproval && isUnapproved) {
                                    const content = getNonApprovedStoriesMessageContent(story);
                                    return this.modalService
                                        .openAlert({
                                            title: this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesTitleAlert],
                                            content: `${this.languageSettings?.[I18nKeys.PSTWebOnlyApprovedStoriesMessageAlert]} ${content}`,
                                            okString: this.languageSettings?.[I18nKeys.PSTWebOkButtonLabel],
                                        })
                                        .result.pipe(switchMap(() => []));
                                }

                                return this.shouldReplaceStoryObservable(destRundown, story).pipe(
                                    map(replaceStoryGuid => {
                                        const storyToRemoveIdx = destRundown.children.findIndex(v => v.Guid === replaceStoryGuid);
                                        if (storyToRemoveIdx > -1) {
                                            insertBeforeGuid = destRundown.children[storyToRemoveIdx + 1]?.Guid;
                                        }
                                        return replaceStoryGuid;
                                    }),
                                    concatMap(replaceStoryGuid => {
                                        if (replaceStoryGuid) {
                                            const storyToRemove = stories.get(replaceStoryGuid);
                                            if (storyToRemove) {
                                                return this.basicContentService
                                                    .deleteBasicContent({ database: storyToRemove.Database, path: storyToRemove.Path?.fullPath, guid: storyToRemove.Guid, type: storyToRemove.Type })
                                                    .pipe(map(res => ({ success: !!res.status, storyToRemoveGuid: storyToRemove?.Guid, deletedResponse: res.data })));
                                            }
                                        }
                                        return of({ success: true, storyToRemoveGuid: null as string | null, deletedResponse: null as RemoveRecordResponse | null });
                                    }),
                                    filter(res => !!res.success),
                                    concatMap(({ storyToRemoveGuid, deletedResponse }) => {
                                        // Check for duplicate slugs
                                        const text = story?.Text;
                                        const textCommands = story?.TextCommands;
                                        const propertiesToOverride = {};
                                        propertiesToOverride[PSTFieldNames.PSTDraftText] = RichTextSerializer.instance.serialize(text);
                                        propertiesToOverride[PSTFieldNames.Title] = story?.Title;
                                        if (story?.PSTPrimaryStoryGUID) {
                                            propertiesToOverride[PSTFieldNames.PSTPrimaryStoryGUID] = null;
                                            propertiesToOverride[PSTFieldNames.PSTSubstoriesSequence] = 0;
                                        }
                                        if (textCommands) {
                                            const textCommandKeys = Object.keys(textCommands);
                                            const textCommandValues = Object.values(textCommands);
                                            if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                                propertiesToOverride['TextCommandKeys'] = textCommandKeys;
                                                propertiesToOverride['TextCommandValues'] = textCommandValues;
                                            }
                                        }
                                        const actions: Action[] = [];
                                        actions.push(
                                            LiveApiActions.reloadRundowns({
                                                sourceROs: [
                                                    {
                                                        Guid: updatedDestRundown?.Guid,
                                                        Database: updatedDestRundown?.Database,
                                                        Path: updatedDestRundown?.Path,
                                                    },
                                                ],
                                            })
                                        );
                                        const updatedRundown = deletedResponse?.updatedRecords?.rundowns?.get(updatedDestRundown?.Guid);
                                        if (updatedRundown) {
                                            updatedDestRundown.Sequence = [...updatedRundown.Sequence];
                                            updatedDestRundown.children = [...updatedRundown.children];
                                        }
                                        if (storyToRemoveGuid) {
                                            actions.push(StoriesRecordActions.deleteStories({ records: [{ Guid: storyToRemoveGuid }] }));
                                        }
                                        if (story) {
                                            return from(
                                                this.nomWebServicesJson.copyScriptToCollection(
                                                    destRundown.Database,
                                                    destRundown.Path?.fullPath,
                                                    destRundown.Guid,
                                                    ObjectTypes.RUNDOWN,
                                                    story.Database,
                                                    story.Path?.fullPath,
                                                    story.Guid,
                                                    ObjectTypes.SCRIPT,
                                                    this.nomWebServicesJson.convertor.toENPSFields(propertiesToOverride),
                                                    insertBeforeGuid
                                                )
                                            ).pipe(
                                                exhaustMap(copyResponse => {
                                                    if (copyResponse.status && copyResponse.data) {
                                                        if (copyResponse.data?.additionalData?.sequence) {
                                                            updatedDestRundown.Sequence = copyResponse.data?.additionalData?.sequence;
                                                        }
                                                        const newStoryIdx = updatedDestRundown.Sequence.findIndex(storyGuid => storyGuid === copyResponse.data.record.Guid);

                                                        const newChildren = [...updatedDestRundown.children];
                                                        if (newStoryIdx > -1) {
                                                            newChildren.splice(newStoryIdx, 0, copyResponse.data.record);
                                                        }
                                                        updatedDestRundown.children = newChildren;
                                                        actions.push(
                                                            StoriesRecordActions.updateStories({
                                                                records: [copyResponse.data.record],
                                                            })
                                                        );
                                                    }
                                                    actions.push(
                                                        RundownRecordActions.updateRundowns({
                                                            records: [updatedDestRundown],
                                                        })
                                                    );

                                                    return of(actions);
                                                })
                                            );
                                        }
                                        return of(actions);
                                    })
                                );
                            })
                        )
                    ),
                    toArray(),
                    map(actionsArrays => actionsArrays.flat()),
                    mergeMap(actions => (actions.length > 0 ? from(actions) : of(ApplicationActions.noOpAction())))
                );
            })
        );
    });

    private rundownsToReload = new Set<string>();
    onReloadRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.reloadRundowns),
            map(action => {
                action.sourceROs.forEach(ro => {
                    this.rundownsToReload.add(ro.Guid);
                });
                return {
                    rundownsToReload: this.rundownsToReload,
                    sourceROs: action.sourceROs,
                };
            }),
            debounceTime(RELOAD_DEBOUNCE_TIME),
            map(data => {
                return {
                    roGuids: Array.from(data.rundownsToReload.entries()).map(([guid]) => guid),
                    sourceROs: data.sourceROs,
                };
            }),
            map(data => {
                this.rundownsToReload.clear();
                return data;
            }),
            exhaustMap(data => {
                if (!data.roGuids.length) {
                    return of([]);
                }
                return forkJoin([
                    ...(data.roGuids?.map(guid => {
                        const matchingRundown = data?.sourceROs?.find(r => r.Guid === guid);
                        const roDatabase = matchingRundown?.Database;
                        const roPath = matchingRundown?.Path?.fullPath;
                        if (!roDatabase || !roPath) {
                            return [];
                        }
                        return this.rundownService.getRundown(roDatabase, roPath, guid);
                    }) ?? []),
                ]).pipe(
                    filter(i => !!i),
                    map(responses => {
                        const ret: StorytellingRundownRecord[] = [];
                        responses
                            .filter(i => i.status)
                            .forEach(serviceResponse => {
                                ret.push(serviceResponse.data);
                            });
                        return ret;
                    })
                );
            }),
            exhaustMap(records => {
                if (!records.length) {
                    return of(ApplicationActions.noOpAction());
                }
                return [RundownRecordActions.updateRundowns({ records }), StoriesRecordActions.updateStories({ records: records.map(r => r.children).flat() })];
            })
        );
    });

    openMosStorySendDialog$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LiveApiActions.openMOSStorySendDialog),
                map(action => {
                    const useMosStartTime = this.store.selectSignal(selectUseMosStartTime)();
                    const width = useMosStartTime ? '38.625rem' : '19.3125rem';
                    const height = '33.25rem';
                    this.dialog.open(StMosStorySendComponent, {
                        data: { guid: action.guid },
                        height,
                        width,
                        hasBackdrop: true,
                        disableClose: true,
                        autoFocus: false,
                    });
                })
            );
        },
        { dispatch: false }
    );

    mosStorySend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.mosStorySend),
            switchMap(action => {
                const allRecords = this.store.selectSignal(selectRundownRecords)();
                const record = allRecords.find(i => i.Guid === action.guid);

                return of(this.rundownService.mosStorySend(record, action.storySendTargets)).pipe(
                    map(() => {
                        const updatedRundown = {
                            ...record,
                            MOSStorySendTargets: action.storySendTargets,
                        };
                        return RundownRecordActions.updateRundowns({ records: [updatedRundown] });
                    }),
                    catchError(error => {
                        this.nomWebServicesJson.showErrorModal({
                            message: 'Unable to send mos story',
                            detailMessage: `Unable to send story. \r\nStackTrace: RundownService.mosStorySend(); \r\nError: ${error}`,
                        });
                        return throwError(() => error);
                    })
                );
            })
        );
    });

    private getFullStoryRecord(story: Partial<StorytellingStoryRecord>, forceReloadStory?: boolean): Observable<StorytellingStoryRecord> {
        const storyRecord = this.store.selectSignal(selectStoryRecordByGuid(story?.Guid))();
        if (storyRecord && !forceReloadStory) {
            return of(storyRecord);
        }
        return this.basicContentService.getBasicContent<StorytellingStoryRecord>({ database: story.Database, path: story.Path?.fullPath, guid: story.Guid }).pipe(map(response => response.data));
    }
}
